import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { listingsAPI, metaAPI } from '../lib/api';
import ListingCard from '../components/ListingCard';
import { Search, Car, Truck, Bike, ArrowRight, ChevronRight, Caravan, Cog, Container, Ship, Users, Globe, CheckCircle, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const CATEGORY_ICONS = {
  cars_suvs: Car, trucks_buses: Truck, caravans_campers: Caravan,
  motorcycles_bikes: Bike, industrial: Cog, car_transport: Container, yachts_boats: Ship
};

function AnimatedCounter({ end, duration = 2000, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = 0;
          const startTime = performance.now();
          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(start + (end - start) * eased));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function Landing() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [latestListings, setLatestListings] = useState([]);
  const [stats, setStats] = useState({ total_listings: 0, total_users: 0, total_countries: 37 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      listingsAPI.getAll({ limit: 4, sort_by: 'newest' }).then(r => setLatestListings(r.data.listings)),
      metaAPI.getStats().then(r => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/listings?search=${encodeURIComponent(search.trim())}`);
    else navigate('/listings');
  };

  const categories = ['cars_suvs', 'trucks_buses', 'caravans_campers', 'motorcycles_bikes', 'industrial', 'car_transport', 'yachts_boats'];
  const categoryImages = {
    cars_suvs: "https://images.unsplash.com/photo-1738748140408-61b8965748b8?w=300&h=200&fit=crop",
    trucks_buses: "https://images.unsplash.com/photo-1768352841908-b7a62404b714?w=300&h=200&fit=crop",
    caravans_campers: "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=300&h=200&fit=crop",
    motorcycles_bikes: "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=300&h=200&fit=crop",
    industrial: "https://images.unsplash.com/photo-1759745125322-4585074bffe5?w=300&h=200&fit=crop",
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="landing-page">
      {/* Hero with Banner Background + Categories */}
      <section className="relative overflow-hidden bg-[#0A3A82]" data-testid="hero-section">
        {/* Banner Background Image */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/784e6024-b773-4ac4-8290-84935b3e2d10/images/6c004be7ed6dd429f2b7bb903985b412c82dd1d97b2a02eb952ae1d02e00f420.png"
            alt=""
            className="w-full h-full object-cover opacity-30"
            data-testid="hero-banner-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A3A82] via-[#0A3A82]/80 to-[#0A3A82]/40" />
        </div>

        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left: Text + Search */}
            <div className="lg:col-span-7">
              <div className="flex gap-1 mb-5">
                {[...Array(12)].map((_, i) => (
                  <span key={i} className="text-[#FADB5F] text-sm">&#9733;</span>
                ))}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('hero.title')}
              </h1>
              <p className="text-base md:text-lg text-white/60 mb-8 max-w-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('hero.subtitle')}
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex gap-2 max-w-lg" data-testid="hero-search-form">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('hero.search')}
                    className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-[#FADB5F]"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="h-12 px-6 bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="hero-search-button">
                  {t('hero.searchBtn')}
                </Button>
              </form>
            </div>

            {/* Right: Categories stacked */}
            <div className="lg:col-span-5" data-testid="categories-section">
              <div className="flex flex-col gap-2">
                {categories.map(cat => {
                  const Icon = CATEGORY_ICONS[cat] || Car;
                  return (
                    <Link
                      key={cat}
                      to={`/listings?vehicle_type=${cat}`}
                      className="group flex items-center gap-3 px-5 py-3.5 rounded-xl bg-white/[0.07] border border-white/10 backdrop-blur-sm hover:bg-white/[0.14] hover:border-[#FADB5F]/40 transition-all duration-200"
                      data-testid={`category-${cat}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#FADB5F]/15 flex items-center justify-center shrink-0 group-hover:bg-[#FADB5F]/25 transition-colors">
                        <Icon className="w-5 h-5 text-[#FADB5F]" />
                      </div>
                      <span className="text-sm font-semibold text-white/90 group-hover:text-white transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {t(`categories.${cat}`)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-white/30 ml-auto group-hover:text-[#FADB5F] group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By + Stats Strip */}
      <section className="bg-[#111827] border-b border-[#1E293B] py-8" data-testid="stats-section">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Trusted By Banner */}
          <div className="flex items-center justify-center gap-3 mb-6" data-testid="trusted-by-banner">
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent to-[#FADB5F]/30" />
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FADB5F]/10 border border-[#FADB5F]/20">
              <CheckCircle className="w-4 h-4 text-[#FADB5F]" />
              <span className="text-sm font-semibold text-[#FADB5F] tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Trusted by <AnimatedCounter end={Math.max(stats.total_users, 50)} duration={2500} suffix="+" /> users across Europe
              </span>
            </div>
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-l from-transparent to-[#FADB5F]/30" />
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="group" data-testid="stat-listings">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Car className="w-4 h-4 text-[#FADB5F]/60" />
                <p className="text-2xl sm:text-3xl font-bold text-[#FADB5F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <AnimatedCounter end={stats.total_listings} duration={2000} />
                </p>
              </div>
              <p className="text-xs sm:text-sm text-[#94A3B8]">{t('stats.listings')}</p>
            </div>
            <div className="group" data-testid="stat-users">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users className="w-4 h-4 text-[#FADB5F]/60" />
                <p className="text-2xl sm:text-3xl font-bold text-[#FADB5F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <AnimatedCounter end={stats.total_users} duration={2200} suffix="+" />
                </p>
              </div>
              <p className="text-xs sm:text-sm text-[#94A3B8]">{t('stats.users')}</p>
            </div>
            <div className="group" data-testid="stat-countries">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Globe className="w-4 h-4 text-[#FADB5F]/60" />
                <p className="text-2xl sm:text-3xl font-bold text-[#FADB5F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <AnimatedCounter end={stats.total_countries} duration={1800} suffix="+" />
                </p>
              </div>
              <p className="text-xs sm:text-sm text-[#94A3B8]">{t('stats.countries')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Categories Gallery */}
      <section className="py-12 bg-[#0B0F19]" data-testid="categories-gallery-section">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Top row: 3 animated cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {/* Cars & SUVs - Audi driving */}
            <Link
              to="/listings?vehicle_type=cars_suvs"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30"
              data-testid="category-img-cars_suvs"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1773793097960-5dbdcbc081c0?w=600&h=340&fit=crop"
                  alt="Cars & SUVs"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'driveRight 12s ease-in-out infinite alternate' }}
                />
                {/* Speed lines overlay */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(250,219,95,0.05) 40px, rgba(250,219,95,0.05) 42px)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.cars_suvs')}
                  </span>
                </div>
              </div>
            </Link>

            {/* Trucks & Buses - Scania driving */}
            <Link
              to="/listings?vehicle_type=trucks_buses"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30"
              data-testid="category-img-trucks_buses"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1670509295484-df0c2512fec4?w=600&h=340&fit=crop"
                  alt="Trucks & Buses"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'driveRight 15s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(250,219,95,0.04) 60px, rgba(250,219,95,0.04) 63px)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.trucks_buses')}
                  </span>
                </div>
              </div>
            </Link>

            {/* Motorcycles & Bikes - Electric scooter */}
            <Link
              to="/listings?vehicle_type=motorcycles_bikes"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30"
              data-testid="category-img-motorcycles_bikes"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1721734381621-1adb7da480e7?w=600&h=340&fit=crop"
                  alt="Motorcycles & Bikes"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'kenBurns 10s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Bike className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.motorcycles_bikes')}
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Bottom row: 2 remaining categories */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Caravans & Campers */}
            <Link
              to="/listings?vehicle_type=caravans_campers"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30"
              data-testid="category-img-caravans_campers"
            >
              <div className="aspect-[2/1] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=600&h=300&fit=crop"
                  alt="Caravans & Campers"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'kenBurns 14s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/20 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Caravan className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.caravans_campers')}
                  </span>
                </div>
              </div>
            </Link>

            {/* Industrial Machines */}
            <Link
              to="/listings?vehicle_type=industrial"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30"
              data-testid="category-img-industrial"
            >
              <div className="aspect-[2/1] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1759745125322-4585074bffe5?w=600&h=300&fit=crop"
                  alt="Industrial Machines"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'kenBurns 16s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/20 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Cog className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.industrial')}
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Car Transport + Yachts - full width row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Link
              to="/listings?vehicle_type=car_transport"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30 block"
              data-testid="category-img-car_transport"
            >
              <div className="aspect-[2/1] overflow-hidden relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_auto-buy-sell-6/artifacts/jts5h8dc_enclosed-2166501792.webp"
                  alt="Car Transport"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'driveRight 18s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Container className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.car_transport')}
                  </span>
                </div>
              </div>
            </Link>

            <Link
              to="/listings?vehicle_type=yachts_boats"
              className="group relative overflow-hidden rounded-xl border border-[#1E293B] bg-[#111827] hover:-translate-y-1 transition-all duration-300 hover:shadow-lg hover:shadow-[#FADB5F]/10 hover:border-[#FADB5F]/30 block"
              data-testid="category-img-yachts_boats"
            >
              <div className="aspect-[2/1] overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=600&h=300&fit=crop"
                  alt="Yachts & Boats"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                  style={{ animation: 'kenBurns 14s ease-in-out infinite alternate' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2">
                  <Ship className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('categories.yachts_boats')}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Listings */}
      {latestListings.length > 0 && (
        <section className="py-16 bg-[#111827]" data-testid="latest-listings-section">
          <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('filters.newest')}
              </h2>
              <Link to="/listings" className="flex items-center gap-1 text-sm font-medium text-[#FADB5F] hover:underline" data-testid="view-all-link">
                {t('common.viewAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {latestListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Download App Section */}
      <section className="py-16 bg-[#0B0F19] border-t border-[#1E293B]" data-testid="download-section">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A3A82] to-[#061E45] border border-[#1E3A6E] p-8 sm:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none">
              {[...Array(12)].map((_, i) => {
                const angle = (i * 30 - 90) * (Math.PI / 180);
                const r = 40;
                return (
                  <span key={i} className="absolute text-[#FADB5F] text-3xl" style={{
                    left: `${50 + r * Math.cos(angle)}%`,
                    top: `${50 + r * Math.sin(angle)}%`,
                    transform: 'translate(-50%, -50%)'
                  }}>&#9733;</span>
                );
              })}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Smartphone className="w-5 h-5 text-[#FADB5F]" />
                  <span className="text-sm font-semibold text-[#FADB5F] uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Mobile App
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'bg' ? 'Свалете EuroMoto' : 'Download EuroMoto'}
                </h2>
                <p className="text-base text-[#94A3B8] mb-6 max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {lang === 'bg'
                    ? 'Инсталирайте приложението на вашия Android телефон за бърз достъп до обяви за превозни средства в цяла Европа.'
                    : 'Install the app on your Android phone for quick access to vehicle listings across Europe.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/EuroMoto.apk"
                    download="EuroMoto.apk"
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#FADB5F] text-[#0F172A] font-semibold hover:bg-[#E5C34A] transition-colors"
                    data-testid="download-apk-btn"
                  >
                    <Download className="w-5 h-5" />
                    {lang === 'bg' ? 'Свали APK (Android)' : 'Download APK (Android)'}
                  </a>
                </div>
                <p className="text-xs text-[#64748B] mt-3">
                  v1.0.0 • 936 KB • Android 5.0+
                </p>
              </div>
              <div className="hidden lg:flex justify-center">
                <div className="relative">
                  <div className="w-48 h-48 rounded-3xl bg-[#0B0F19]/60 border border-[#1E3A6E] flex items-center justify-center shadow-2xl shadow-black/40">
                    <img
                      src="/euromoto_icon_512.png"
                      alt="EuroMoto App"
                      className="w-32 h-32 rounded-2xl"
                      data-testid="download-app-icon"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    FREE
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA with Feature Graphic */}
      <section className="py-16 bg-[#0A3A82] relative overflow-hidden" data-testid="cta-section">
        {/* Feature Graphic Background */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/784e6024-b773-4ac4-8290-84935b3e2d10/images/3ece8f35c28a4c487908599ecb50582b768a2de069bc9519edfdc5c62ad30c7e.png"
            alt=""
            className="w-full h-full object-cover opacity-20"
            data-testid="cta-feature-graphic"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A3A82] via-[#0A3A82]/70 to-[#0A3A82]/90" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: Feature Graphic visible */}
            <div className="hidden lg:block">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/784e6024-b773-4ac4-8290-84935b3e2d10/images/3ece8f35c28a4c487908599ecb50582b768a2de069bc9519edfdc5c62ad30c7e.png"
                alt="EuroMoto App"
                className="w-full max-w-md mx-auto rounded-2xl shadow-2xl shadow-black/40 border border-white/10"
                data-testid="cta-feature-image"
              />
            </div>
            {/* Right: CTA Text */}
            <div className="text-center lg:text-left">
              <div className="flex justify-center lg:justify-start gap-1 mb-6">
                {[...Array(12)].map((_, i) => (
                  <span key={i} className="text-[#FADB5F] text-sm">&#9733;</span>
                ))}
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('create.title')}
              </h2>
              <p className="text-base text-[#94A3B8] mb-8 max-w-lg mx-auto lg:mx-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('payment.promoteDesc')}
              </p>
              <Link to="/create-listing" data-testid="cta-create-btn">
                <Button className="h-12 px-8 bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold text-base">
                  {t('nav.create')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
